import { memo, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNotify, usePermissions, useTranslate } from 'react-admin';

import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { DashboardContent } from 'src/layouts/dashboard';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { Iconify } from 'src/components/iconify';
import { PermissionButton, PermissionGuard } from 'src/routes/components';
import {
  AmountRequestsApi,
  AmountRequestsPanel,
  OverrideAmountDialog,
} from './AmountRequestsPanel';
import type { MoneyRequest } from 'src/pages/admin/services/moneyRequestApi';
import type { PermissionData } from 'src/pages/admin/entities/permission.entity';
import { hasPermission } from 'src/pages/admin/utils/has-permission';

import { useLeaderboardConfig } from './hooks/useLeaderboardConfig';
import type {
  CycleStatus,
  FieldProps,
  LeaderboardCycle,
  LeaderboardCycleDraft,
  LeaderboardDefinition,
  LeaderboardDefinitionId,
  LeaderboardPageTab,
  LeaderboardTierDraft,
  SaveBarProps,
  SettlementEntry,
  SettlementSummary,
  TierTableProps,
  TranslateFn,
  LeaderboardPeriod,
} from './types';
import {
  buildReplaceTiersPayload,
  createEmptyTier,
  formatDateTime,
  formatRankRange,
  formatUsdc,
  fromDateInputEndOfDay,
  fromDateInputStartOfDay,
  getErrorMessage,
  isTierRowsEqual,
  parseRankRange,
  toDateInput,
  toDraftRows,
} from './utils';

const I18N_PREFIX = 'module.worldCup.leaderboard';

const tLeaderboard = (t: TranslateFn, key: string, options?: Record<string, unknown>) =>
  t(`${I18N_PREFIX}.${key}`, options);

const PAGE_TABS: { value: LeaderboardPageTab; labelKey: string }[] = [
  { value: 'cycle', labelKey: 'page.cycle' },
  { value: 'reward', labelKey: 'page.reward' },
  { value: 'settlement', labelKey: 'page.settlement' },
];

const CYCLE_STATUS_COLOR: Record<CycleStatus, 'success' | 'warning' | 'info'> = {
  ended: 'warning',
  upcoming: 'info',
  active: 'success',
};

export const MainPage = () => {
  const t = useTranslate();
  const notify = useNotify();

  const [pageTab, setPageTab] = useState<LeaderboardPageTab>('cycle');

  // === Cycle Management state (tab 1) — placeholder, awaiting API ===
  const [cycleDrafts, setCycleDrafts] = useState<LeaderboardCycleDraft[]>([]);
  const [originalCycleDrafts, setOriginalCycleDrafts] = useState<LeaderboardCycleDraft[]>([]);

  // === Selected cycle ids — separate for Reward (tab 2) and Settlement (tab 3) ===
  const [selectedRewardCycleId, setSelectedRewardCycleId] = useState<string>('');
  const [selectedSettlementCycleId, setSelectedSettlementCycleId] = useState<string>('');

  // === Reward Configuration state (tab 2) — existing leaderboard config ===
  const [activeId, setActiveId] = useState<LeaderboardDefinitionId>('prediction-volume');

  const {
    definitions,
    tierList,
    periods,
    settlement,
    loading,
    tierLoading,
    settlementLoading,
    saving,
    refetchTiers,
    refetchPeriods,
    refetchSettlement,
    replaceTiers,
    copyTiersFrom,
    applyTiersToAll,
    createPeriod,
    updatePeriod,
    deletePeriod,
    payAllSettlement,
    payEntry,
    retryEntry,
    requestAmountOverride,
    listAmountRequests,
    approveAmountRequest,
    rejectAmountRequest,
    cancelAmountRequest,
    exportRanking,
  } = useLeaderboardConfig(activeId, selectedRewardCycleId, selectedSettlementCycleId);

  const handleExportRanking = useCallback(
    async (periodId: string) => {
      try {
        await exportRanking(periodId);
      } catch (err) {
        notify(getErrorMessage(err) || tLeaderboard(t, 'cycle.notify.exportFailed'), {
          type: 'error',
        });
      }
    },
    [exportRanking, notify, t]
  );

  const previousPeriod = useMemo(() => {
    const current = periods.find((p) => p.id === selectedRewardCycleId);
    if (!current) return null;
    const currentStart = new Date(current.startsAt).getTime();
    if (!Number.isFinite(currentStart)) return null;

    const earlier = periods
      .filter((p) => p.id !== current.id)
      .map((p) => ({ p, ms: new Date(p.startsAt).getTime() }))
      .filter(({ ms }) => Number.isFinite(ms) && ms < currentStart)
      .sort((a, b) => b.ms - a.ms);

    return earlier[0]?.p ?? null;
  }, [periods, selectedRewardCycleId]);

  const [tierDrafts, setTierDrafts] = useState<LeaderboardTierDraft[]>([]);
  const [originalTierDrafts, setOriginalTierDrafts] = useState<LeaderboardTierDraft[]>([]);

  useEffect(() => {
    const drafts: LeaderboardCycleDraft[] = periods.map((p) => ({
      localId: `server-${p.id}`,
      id: p.id,
      name: p.name,
      startsAt: p.startsAt,
      endsAt: p.endsAt,
      status: p.status,
    }));
    setCycleDrafts(drafts);
    setOriginalCycleDrafts(drafts);

    const initialReward = () => periods[0]?.id ?? '';
    const initialSettlement = () => periods.find((p) => p.status === 'ended')?.id ?? '';

    setSelectedRewardCycleId((prev) =>
      prev && periods.some((p) => p.id === prev) ? prev : initialReward()
    );
    setSelectedSettlementCycleId((prev) =>
      prev && periods.some((p) => p.id === prev) ? prev : initialSettlement()
    );
  }, [periods]);

  useEffect(() => {
    const rows = toDraftRows(tierList?.tiers ?? []);
    setTierDrafts(rows);
    setOriginalTierDrafts(rows);
  }, [tierList]);

  const isTiersDirty = !isTierRowsEqual(tierDrafts, originalTierDrafts);

  const isTiersInvalid = useMemo(
    () =>
      tierDrafts.some((row, index) => {
        const reward = Number(String(row.rewardPerRank ?? '').trim());
        if (!Number.isFinite(reward) || reward <= 0) return true;
        const from = Number(row.rangeFrom);
        const to = Number(row.rangeTo);
        if (!Number.isFinite(from) || !Number.isFinite(to) || from <= 0 || to <= 0) return true;
        if (to < from) return true;
        const prev = tierDrafts[index - 1];
        if (prev && Number(prev.rangeTo) >= from) return true;
        return false;
      }),
    [tierDrafts]
  );

  const isCycleDirty = useMemo(() => {
    if (cycleDrafts.length !== originalCycleDrafts.length) return true;
    const origMap = new Map(originalCycleDrafts.map((item) => [item.localId, item]));
    return cycleDrafts.some((item) => {
      const orig = origMap.get(item.localId);
      return (
        !orig ||
        item.name !== orig.name ||
        item.startsAt !== orig.startsAt ||
        item.endsAt !== orig.endsAt
      );
    });
  }, [cycleDrafts, originalCycleDrafts]);

  const isCycleInvalid = useMemo(
    () =>
      cycleDrafts.some((item, index) => {
        if (!item.name.trim()) return true;
        const startMs = item.startsAt ? new Date(item.startsAt).getTime() : NaN;
        const endMs = item.endsAt ? new Date(item.endsAt).getTime() : NaN;
        if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return true;
        if (endMs <= startMs) return true;
        const prev = cycleDrafts[index - 1];
        const prevEndMs = prev?.endsAt ? new Date(prev.endsAt).getTime() : NaN;
        if (Number.isFinite(prevEndMs) && startMs < prevEndMs) return true;
        return false;
      }),
    [cycleDrafts]
  );

  const hasUnsavedChanges =
    pageTab === 'cycle' ? isCycleDirty : pageTab === 'reward' ? isTiersDirty : false;

  const handleDiscard = useCallback(() => {
    if (!window.confirm(tLeaderboard(t, 'confirm.discard'))) return;

    if (pageTab === 'cycle') {
      setCycleDrafts(originalCycleDrafts);
    } else if (pageTab === 'reward') {
      setTierDrafts(originalTierDrafts);
    }
  }, [originalCycleDrafts, originalTierDrafts, pageTab, t]);

  const handleSave = useCallback(async () => {
    try {
      if (pageTab === 'cycle') {
        if (isCycleInvalid) {
          notify(tLeaderboard(t, 'cycle.error.invalid'), { type: 'warning' });
          return;
        }
        const origMap = new Map(originalCycleDrafts.map((item) => [item.localId, item]));
        const draftIds = new Set(cycleDrafts.map((item) => item.localId));

        const toCreate = cycleDrafts.filter((item) => !item.id);
        const toUpdate = cycleDrafts.filter((item) => {
          if (!item.id) return false;
          const orig = origMap.get(item.localId);
          if (!orig) return false;
          return (
            item.name !== orig.name ||
            item.startsAt !== orig.startsAt ||
            item.endsAt !== orig.endsAt
          );
        });
        const toDelete = originalCycleDrafts.filter(
          (item) => item.id && !draftIds.has(item.localId)
        );

        await Promise.all([
          ...toCreate.map((item) =>
            createPeriod({ name: item.name, startsAt: item.startsAt, endsAt: item.endsAt })
          ),
          ...toUpdate.map((item) =>
            updatePeriod(item.id, {
              name: item.name,
              startsAt: item.startsAt,
              endsAt: item.endsAt,
            })
          ),
          ...toDelete.map((item) => deletePeriod(item.id)),
        ]);

        await refetchPeriods();
        notify(tLeaderboard(t, 'notify.saveSuccess'), { type: 'success' });
        return;
      }

      if (pageTab === 'reward') {
        if (!selectedRewardCycleId) {
          notify(tLeaderboard(t, 'reward.error.cycleRequired'), { type: 'warning' });
          return;
        }
        if (isTiersDirty && isTiersInvalid) {
          notify(tLeaderboard(t, 'reward.error.tierInvalid'), { type: 'warning' });
          return;
        }
        if (isTiersDirty) {
          await replaceTiers(selectedRewardCycleId, activeId, buildReplaceTiersPayload(tierDrafts));
        }
        await refetchTiers();
        notify(tLeaderboard(t, 'notify.saveSuccess'), { type: 'success' });
      }
    } catch (err) {
      notify(getErrorMessage(err) || tLeaderboard(t, 'notify.saveFailed'), { type: 'error' });
    }
  }, [
    activeId,
    createPeriod,
    cycleDrafts,
    deletePeriod,
    isCycleInvalid,
    isTiersDirty,
    isTiersInvalid,
    notify,
    originalCycleDrafts,
    pageTab,
    refetchPeriods,
    selectedRewardCycleId,
    updatePeriod,
    refetchTiers,
    replaceTiers,
    t,
    tierDrafts,
  ]);

  const handleCopyPrevious = useCallback(async () => {
    if (!selectedRewardCycleId) {
      notify(tLeaderboard(t, 'reward.error.cycleRequired'), { type: 'warning' });
      return;
    }
    if (!previousPeriod) {
      notify(tLeaderboard(t, 'reward.error.noPreviousCycle'), { type: 'warning' });
      return;
    }
    if (!window.confirm(tLeaderboard(t, 'reward.confirm.copyPrevious'))) return;
    try {
      await copyTiersFrom(selectedRewardCycleId, activeId, previousPeriod.id);
      await refetchTiers();
      notify(tLeaderboard(t, 'reward.notify.copySuccess'), { type: 'success' });
    } catch (err) {
      notify(getErrorMessage(err) || tLeaderboard(t, 'reward.notify.copyFailed'), {
        type: 'error',
      });
    }
  }, [activeId, copyTiersFrom, notify, previousPeriod, refetchTiers, selectedRewardCycleId, t]);

  const handlePayAll = useCallback(async () => {
    if (!selectedSettlementCycleId) {
      notify(tLeaderboard(t, 'reward.error.cycleRequired'), { type: 'warning' });
      return;
    }
    if (!window.confirm(tLeaderboard(t, 'settlement.confirm.payAll'))) return;
    try {
      await payAllSettlement(selectedSettlementCycleId, activeId);
      await refetchSettlement();
      notify(tLeaderboard(t, 'settlement.notify.payAllSuccess'), { type: 'success' });
    } catch (err) {
      notify(getErrorMessage(err) || tLeaderboard(t, 'settlement.notify.payAllFailed'), {
        type: 'error',
      });
    }
  }, [activeId, notify, payAllSettlement, refetchSettlement, selectedSettlementCycleId, t]);

  const handlePayEntry = useCallback(
    async (entryId: string) => {
      try {
        await payEntry(entryId);
        await refetchSettlement();
        notify(tLeaderboard(t, 'settlement.notify.paySuccess'), { type: 'success' });
      } catch (err) {
        notify(getErrorMessage(err) || tLeaderboard(t, 'settlement.notify.payFailed'), {
          type: 'error',
        });
      }
    },
    [notify, payEntry, refetchSettlement, t]
  );

  const handleRetryEntry = useCallback(
    async (entryId: string) => {
      try {
        await retryEntry(entryId);
        await refetchSettlement();
        notify(tLeaderboard(t, 'settlement.notify.retrySuccess'), { type: 'success' });
      } catch (err) {
        notify(getErrorMessage(err) || tLeaderboard(t, 'settlement.notify.retryFailed'), {
          type: 'error',
        });
      }
    },
    [notify, refetchSettlement, retryEntry, t]
  );

  const handleApplyAll = useCallback(async () => {
    if (!selectedRewardCycleId) {
      notify(tLeaderboard(t, 'reward.error.cycleRequired'), { type: 'warning' });
      return;
    }
    if (!window.confirm(tLeaderboard(t, 'reward.confirm.applyAll'))) return;
    try {
      await applyTiersToAll(selectedRewardCycleId, activeId);
      await refetchTiers();
      notify(tLeaderboard(t, 'reward.notify.applyAllSuccess'), { type: 'success' });
    } catch (err) {
      notify(getErrorMessage(err) || tLeaderboard(t, 'reward.notify.applyAllFailed'), {
        type: 'error',
      });
    }
  }, [activeId, applyTiersToAll, notify, refetchTiers, selectedRewardCycleId, t]);

  return (
    <DashboardContent>
      <CustomBreadcrumbs
        heading={tLeaderboard(t, 'title')}
        links={[
          { name: t('resource.worldCup') },
          { name: t('resource.worldCupLeaderboardConfig') },
        ]}
        sx={{ mb: 3 }}
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {tLeaderboard(t, 'description')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontStyle: 'italic' }}>
        {tLeaderboard(t, 'note')}
      </Typography>

      <Paper
        variant="outlined"
        sx={{
          borderTop: 'none',
          borderLeft: 'none',
          borderRight: 'none',
          mb: 3,
          display: 'inline-flex',
        }}
      >
        <Tabs
          value={pageTab}
          onChange={(_, next: LeaderboardPageTab) => setPageTab(next)}
          sx={{ minHeight: 44, '& .MuiTab-root': { minHeight: 44, borderRadius: 999, px: 3 } }}
        >
          {PAGE_TABS.map((tab) => (
            <Tab key={tab.value} value={tab.value} label={tLeaderboard(t, tab.labelKey)} />
          ))}
        </Tabs>
      </Paper>

      {loading ? (
        <CenterBox>
          <CircularProgress size={28} />
        </CenterBox>
      ) : (
        <Stack spacing={3}>
          {pageTab === 'cycle' ? (
            <CyclePanel
              cycles={cycleDrafts}
              onCyclesChange={setCycleDrafts}
              onExport={handleExportRanking}
              t={t}
            />
          ) : null}

          {pageTab === 'reward' ? (
            <RewardPanel
              definitions={definitions}
              tierDrafts={tierDrafts}
              tierLoading={tierLoading}
              periods={periods}
              selectedCycleId={selectedRewardCycleId}
              previousPeriod={previousPeriod}
              activeId={activeId}
              onCycleChange={setSelectedRewardCycleId}
              onDefinitionChange={setActiveId}
              onRowsChange={setTierDrafts}
              onCopyPrevious={handleCopyPrevious}
              onApplyAll={handleApplyAll}
              t={t}
            />
          ) : null}

          {pageTab === 'settlement' ? (
            <SettlementPanel
              periods={periods}
              definitions={definitions}
              selectedCycleId={selectedSettlementCycleId}
              activeId={activeId}
              settlement={settlement}
              settlementLoading={settlementLoading}
              onCycleChange={setSelectedSettlementCycleId}
              onDefinitionChange={setActiveId}
              onPayAll={handlePayAll}
              onPayEntry={handlePayEntry}
              onRetryEntry={handleRetryEntry}
              onRefresh={refetchSettlement}
              amountApi={{
                list: listAmountRequests,
                approve: approveAmountRequest,
                reject: rejectAmountRequest,
                cancel: cancelAmountRequest,
              }}
              onRequestAmount={requestAmountOverride}
              t={t}
            />
          ) : null}

          {hasUnsavedChanges ? (
            <SaveBar
              loading={saving}
              onDiscard={handleDiscard}
              onSave={handleSave}
              requiredAnyOf={
                pageTab === 'cycle'
                  ? // the cycle tab writes the period routes
                    [
                      { resource: 'world-cup-leaderboard-period', action: 'write' },
                      { resource: 'world-cup-leaderboard', action: 'write' },
                    ]
                  : // the reward tab writes PUT .../tiers, which only accepts world-cup-leaderboard:write
                    [{ resource: 'world-cup-leaderboard', action: 'write' }]
              }
            />
          ) : null}
        </Stack>
      )}
    </DashboardContent>
  );
};

// ============ Cycle Management Panel (Tab 1) ============

const CyclePanel = memo(
  ({
    cycles,
    onCyclesChange,
    onExport,
    t,
  }: {
    cycles: LeaderboardCycleDraft[];
    onCyclesChange: (next: LeaderboardCycleDraft[]) => void;
    onExport: (periodId: string) => void | Promise<void>;
    t: TranslateFn;
  }) => {
    const handleAdd = () => {
      const localId = `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      onCyclesChange([
        ...cycles,
        {
          id: '',
          localId,
          name: `Phase ${cycles.length + 1}`,
          startsAt: '',
          endsAt: '',
          status: 'upcoming',
        },
      ]);
    };

    const handleRemove = (localId: string) => {
      onCyclesChange(cycles.filter((item) => item.localId !== localId));
    };

    const handleChange = (localId: string, patch: Partial<LeaderboardCycle>) => {
      onCyclesChange(
        cycles.map((item) => (item.localId === localId ? { ...item, ...patch } : item))
      );
    };

    return (
      <Card sx={{ p: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ mb: 2 }}
        >
          <Stack>
            <Typography variant="h6">{tLeaderboard(t, 'cycle.title')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {tLeaderboard(t, 'cycle.desc')}
            </Typography>
          </Stack>

          <Button
            variant="outlined"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={handleAdd}
          >
            {tLeaderboard(t, 'cycle.add')}
          </Button>
        </Stack>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 160 }}>{tLeaderboard(t, 'cycle.col.name')}</TableCell>
                <TableCell>{tLeaderboard(t, 'cycle.col.startAt')}</TableCell>
                <TableCell>{tLeaderboard(t, 'cycle.col.endAt')}</TableCell>
                <TableCell sx={{ width: 140 }}>{tLeaderboard(t, 'cycle.col.status')}</TableCell>
                <TableCell sx={{ width: 200 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {cycles.length ? (
                cycles.map((row, index) => {
                  const prev = cycles[index - 1];
                  const prevEndsAt = prev?.endsAt ?? '';

                  const startMs = row.startsAt ? new Date(row.startsAt).getTime() : NaN;
                  const endMs = row.endsAt ? new Date(row.endsAt).getTime() : NaN;
                  const prevEndMs = prevEndsAt ? new Date(prevEndsAt).getTime() : NaN;

                  const startMissing = !row.startsAt;
                  const endMissing = !row.endsAt;
                  const startBeforePrev =
                    Number.isFinite(startMs) && Number.isFinite(prevEndMs) && startMs < prevEndMs;
                  const endBeforeStart =
                    Number.isFinite(startMs) && Number.isFinite(endMs) && endMs <= startMs;

                  return (
                    <TableRow key={row.localId}>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          required
                          value={row.name}
                          error={!row.name.trim()}
                          // helperText={!row.name.trim() ? t('ra.validation.required') : undefined}
                          onChange={(event) =>
                            handleChange(row.localId, { name: event.target.value })
                          }
                          disabled={row.status === 'ended' || row.status === 'active'}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          type="date"
                          required
                          value={toDateInput(row.startsAt)}
                          error={startMissing || startBeforePrev}
                          // helperText={
                          //   startMissing
                          //     ? t('ra.validation.required')
                          //     : startBeforePrev
                          //       ? tLeaderboard(t, 'cycle.error.startBeforePrev')
                          //       : undefined
                          // }
                          inputProps={{
                            min: prevEndsAt ? toDateInput(prevEndsAt) : undefined,
                          }}
                          onChange={(event) =>
                            handleChange(row.localId, {
                              startsAt: fromDateInputStartOfDay(event.target.value),
                            })
                          }
                          disabled={row.status === 'ended' || row.status === 'active'}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          type="date"
                          required
                          value={toDateInput(row.endsAt)}
                          error={endMissing || endBeforeStart}
                          // helperText={
                          //   endMissing
                          //     ? t('ra.validation.required')
                          //     : endBeforeStart
                          //       ? tLeaderboard(t, 'cycle.error.endBeforeStart')
                          //       : undefined
                          // }
                          inputProps={{
                            min: row.startsAt ? toDateInput(row.startsAt) : undefined,
                          }}
                          onChange={(event) =>
                            handleChange(row.localId, {
                              endsAt: fromDateInputEndOfDay(event.target.value),
                            })
                          }
                          disabled={row.status === 'ended' || row.status === 'active'}
                        />
                      </TableCell>
                      <TableCell>
                        <CycleStatusChip status={row.status} t={t} />
                      </TableCell>
                      <TableCell align="right">
                        {row.status === 'ended' || row.status === 'active' ? (
                          <PermissionButton resource="world-cup-leaderboard" action="export">
                            <Button
                              variant="text"
                              color="primary"
                              size="small"
                              startIcon={<Iconify icon="eva:download-outline" />}
                              onClick={() => onExport(row.id)}
                              sx={{ width: 140 }}
                            >
                              {tLeaderboard(t, 'cycle.exportRanking')}
                            </Button>
                          </PermissionButton>
                        ) : (
                          <PermissionGuard
                            anyOf={[
                              { resource: 'world-cup-leaderboard-period', action: 'write' },
                              { resource: 'world-cup-leaderboard', action: 'write' },
                            ]}
                            fallback={<></>}
                            loadingFallback={<></>}
                          >
                            <Button
                              variant="text"
                              color="error"
                              size="small"
                              onClick={() => handleRemove(row.localId)}
                            >
                              {tLeaderboard(t, 'actions.delete')}
                            </Button>
                          </PermissionGuard>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={5}>
                    <EmptyText text={tLeaderboard(t, 'cycle.empty')} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    );
  }
);

CyclePanel.displayName = 'CyclePanel';

const CycleStatusChip = memo(({ status, t }: { status: CycleStatus; t: TranslateFn }) => {
  const color = CYCLE_STATUS_COLOR[status];
  const label = tLeaderboard(t, `cycle.status.${status}`);

  return (
    <Box
      component="span"
      sx={{
        px: 1.2,
        py: 0.4,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        bgcolor: `${color}.lighter`,
        color: `${color}.dark`,
        display: 'inline-block',
      }}
    >
      {label}
    </Box>
  );
});

CycleStatusChip.displayName = 'CycleStatusChip';

// ============ Reward Configuration Panel (Tab 2) ============

const RewardPanel = memo(
  ({
    definitions,
    tierDrafts,
    tierLoading,
    periods,
    selectedCycleId,
    previousPeriod,
    activeId,
    onCycleChange,
    onDefinitionChange,
    onRowsChange,
    onCopyPrevious,
    onApplyAll,
    t,
  }: {
    definitions: LeaderboardDefinition[];
    tierDrafts: LeaderboardTierDraft[];
    tierLoading: boolean;
    periods: LeaderboardPeriod[];
    selectedCycleId: string;
    previousPeriod: LeaderboardPeriod | null;
    activeId: LeaderboardDefinitionId;
    onCycleChange: (id: string) => void;
    onDefinitionChange: (id: LeaderboardDefinitionId) => void;
    onRowsChange: (rows: LeaderboardTierDraft[]) => void;
    onCopyPrevious: () => void;
    onApplyAll: () => void;
    t: TranslateFn;
  }) => {
    const selectedCycle = periods.find((p) => p.id === selectedCycleId) ?? null;
    const isEnded = selectedCycle?.status === 'ended';
    const isActive = selectedCycle?.status === 'active';

    return (
      <Card sx={{ p: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ mb: 2 }}
        >
          <Stack>
            <Typography variant="h6">{tLeaderboard(t, 'reward.title')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {tLeaderboard(t, 'reward.desc')}
            </Typography>
          </Stack>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr auto' },
            gap: 2,
            alignItems: 'end',
            mb: 2,
          }}
        >
          <Field label={tLeaderboard(t, 'reward.cycle')}>
            <TextField
              select
              size="small"
              fullWidth
              value={selectedCycleId}
              onChange={(event) => onCycleChange(event.target.value)}
            >
              {/* <MenuItem value="">{tLeaderboard(t, 'reward.cyclePlaceholder')}</MenuItem> */}
              {periods.map((period) => (
                <MenuItem key={period.id} value={period.id}>
                  {period.name} · {tLeaderboard(t, `cycle.status.${period.status}`)}
                </MenuItem>
              ))}
            </TextField>
          </Field>

          <Field label={tLeaderboard(t, 'reward.leaderboard')}>
            <TextField
              select
              size="small"
              fullWidth
              value={activeId}
              onChange={(event) =>
                onDefinitionChange(event.target.value as LeaderboardDefinitionId)
              }
            >
              {definitions.map((def) => (
                <MenuItem key={def.id} value={def.id}>
                  {def.description}
                </MenuItem>
              ))}
            </TextField>
          </Field>

          <Stack direction="row" spacing={1}>
            {/* POST .../tiers/copy-from/:fromPeriodId requires world-cup-leaderboard:write */}
            <PermissionButton resource="world-cup-leaderboard" action="write">
              <Button
                variant="outlined"
                size="small"
                disabled={!selectedCycleId || !previousPeriod || isEnded || isActive}
                onClick={onCopyPrevious}
              >
                {tLeaderboard(t, 'reward.copyPrevious')}
              </Button>
            </PermissionButton>
            {/* POST .../tiers/apply-all requires world-cup-leaderboard:write */}
            <PermissionButton resource="world-cup-leaderboard" action="write">
              <Button
                variant="outlined"
                size="small"
                disabled={!selectedCycleId || isEnded || isActive}
                onClick={onApplyAll}
              >
                {tLeaderboard(t, 'reward.applyAll')}
              </Button>
            </PermissionButton>
          </Stack>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {!selectedCycleId ? (
          <EmptyText text={tLeaderboard(t, 'reward.selectCycleHint')} />
        ) : (
          <TierTable rows={tierDrafts} onRowsChange={onRowsChange} disabled={isEnded || isActive} />
        )}

        {tierLoading ? (
          <Stack alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={20} />
          </Stack>
        ) : null}
      </Card>
    );
  }
);

RewardPanel.displayName = 'RewardPanel';

// ============ Settlement Panel (Tab 3) ============

const PAYOUT_STATUS_COLOR: Record<string, 'default' | 'success' | 'warning' | 'info' | 'error'> = {
  none: 'default',
  pending: 'info',
  processing: 'info',
  sent: 'success',
  failed: 'error',
};

const SettlementPanel = memo(
  ({
    periods,
    definitions,
    selectedCycleId,
    activeId,
    settlement,
    settlementLoading,
    onCycleChange,
    onDefinitionChange,
    onPayAll,
    onPayEntry,
    onRetryEntry,
    onRefresh,
    amountApi,
    onRequestAmount,
    t,
  }: {
    periods: LeaderboardPeriod[];
    definitions: LeaderboardDefinition[];
    selectedCycleId: string;
    activeId: LeaderboardDefinitionId;
    settlement: SettlementSummary | null;
    settlementLoading: boolean;
    onCycleChange: (id: string) => void;
    onDefinitionChange: (id: LeaderboardDefinitionId) => void;
    onPayAll: () => void;
    onPayEntry: (entryId: string) => void;
    onRetryEntry: (entryId: string) => void;
    onRefresh: () => void;
    amountApi: AmountRequestsApi;
    onRequestAmount: (
      entryId: string,
      body: { amountUsdc: string; reason?: string }
    ) => Promise<MoneyRequest>;
    t: TranslateFn;
  }) => {
    const [overrideEntry, setOverrideEntry] = useState<SettlementEntry | null>(null);
    const [requestsReloadKey, setRequestsReloadKey] = useState(0);
    const winnerCount = settlement?.winnerCount ?? 0;
    const paidCount = settlement?.paidCount ?? 0;
    const totalDue = settlement?.totalDue ?? '0';
    const paidAmount = settlement?.paidAmount ?? '0';
    const entries = settlement?.entries ?? [];
    const pendingEntries = entries.filter((entry) => entry.payoutStatus !== 'sent');

    return (
      <Card sx={{ p: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ mb: 2 }}
        >
          <Stack>
            <Typography variant="h6">{tLeaderboard(t, 'settlement.title')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {tLeaderboard(t, 'settlement.desc')}
            </Typography>
          </Stack>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: 2,
            mb: 2,
          }}
        >
          <Field label={tLeaderboard(t, 'reward.cycle')}>
            <TextField
              select
              size="small"
              fullWidth
              value={selectedCycleId}
              onChange={(event) => onCycleChange(event.target.value)}
            >
              {/* <MenuItem value="">{tLeaderboard(t, 'reward.cyclePlaceholder')}</MenuItem> */}
              {periods.map((period) => (
                <MenuItem key={period.id} value={period.id} disabled={period.status !== 'ended'}>
                  {period.name} · {tLeaderboard(t, `cycle.status.${period.status}`)}
                </MenuItem>
              ))}
            </TextField>
          </Field>

          <Field label={tLeaderboard(t, 'reward.leaderboard')}>
            <TextField
              select
              size="small"
              fullWidth
              value={activeId}
              onChange={(event) =>
                onDefinitionChange(event.target.value as LeaderboardDefinitionId)
              }
            >
              {definitions.map((def) => (
                <MenuItem key={def.id} value={def.id}>
                  {def.description}
                </MenuItem>
              ))}
            </TextField>
          </Field>
        </Box>

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="center">
            <SummaryStat
              label={tLeaderboard(t, 'settlement.prizeDraw')}
              value={String(winnerCount)}
            />
            <SummaryStat
              label={tLeaderboard(t, 'settlement.distributed')}
              value={`${paidCount} / ${winnerCount}`}
            />
            <SummaryStat
              label={tLeaderboard(t, 'settlement.totalPayable')}
              value={`$${formatUsdc(totalDue)}`}
            />
            <SummaryStat
              label={tLeaderboard(t, 'settlement.alreadyIssued')}
              value={`$${formatUsdc(paidAmount)}`}
            />
            <Box sx={{ flexGrow: 1 }} />
            <PermissionButton resource="world-cup-leaderboard-settlement" action="pay">
              <Button
                variant="contained"
                disabled={!selectedCycleId || pendingEntries.length === 0}
                onClick={onPayAll}
                startIcon={<Iconify icon="eva:gift-fill" />}
              >
                {tLeaderboard(t, 'settlement.distributeAll', {
                  count: pendingEntries.length,
                  amount: `$${formatUsdc(totalDue)}`,
                })}
              </Button>
            </PermissionButton>
            <Button
              variant="outlined"
              disabled={!selectedCycleId || settlementLoading}
              startIcon={<Iconify icon="eva:refresh-fill" />}
              onClick={onRefresh}
              sx={{
                minHeight: 36,
              }}
            >
              {tLeaderboard(t, 'settlement.refresh')}
            </Button>
          </Stack>
        </Paper>

        {!selectedCycleId ? (
          <EmptyText text={tLeaderboard(t, 'reward.selectCycleHint')} />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 70 }}>{tLeaderboard(t, 'settlement.col.rank')}</TableCell>
                  <TableCell>{tLeaderboard(t, 'settlement.col.wallet')}</TableCell>
                  <TableCell>{tLeaderboard(t, 'settlement.col.deserved')}</TableCell>
                  <TableCell sx={{ width: 120 }}>
                    {tLeaderboard(t, 'settlement.col.status')}
                  </TableCell>
                  <TableCell sx={{ width: 240 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5} component="span">
                      {tLeaderboard(t, 'settlement.col.distribution')}
                      <Tooltip
                        title={tLeaderboard(t, 'settlement.col.distributionHint')}
                        arrow
                        placement="top"
                      >
                        <Iconify
                          icon="eva:info-outline"
                          width={18}
                          sx={{ color: 'text.secondary', cursor: 'help' }}
                        />
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ width: 180 }}>
                    {tLeaderboard(t, 'settlement.col.distributionTime')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {settlementLoading ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Stack alignItems="center" sx={{ py: 4 }}>
                        <CircularProgress size={22} />
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : entries.length ? (
                  entries.map((entry) => {
                    const statusKey = entry.payoutStatus ?? 'none';
                    console.log('Entry status:', entry.payoutStatus, statusKey);
                    const statusColor = PAYOUT_STATUS_COLOR[statusKey] ?? 'default';
                    const isSent = entry.payoutStatus === 'sent';
                    const isProcessing = entry.payoutStatus === 'processing';
                    const isFailed = entry.payoutStatus === 'failed';
                    const isPending = entry.payoutStatus === 'pending';

                    return (
                      <TableRow key={entry.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            #{entry.rank}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {entry.address}
                          </Typography>
                        </TableCell>
                        <TableCell>${formatUsdc(entry.amountUsdc)}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Box
                              component="span"
                              sx={{
                                px: 1.2,
                                py: 0.4,
                                borderRadius: 999,
                                fontSize: 12,
                                fontWeight: 700,
                                bgcolor: `${statusColor}.lighter`,
                                color: `${statusColor}.dark`,
                              }}
                            >
                              {tLeaderboard(t, `settlement.status.${statusKey}`)}
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <PermissionButton
                              resource="world-cup-leaderboard-settlement"
                              action="pay"
                            >
                              {isFailed ? (
                                <Button
                                  variant="contained"
                                  size="small"
                                  color="warning"
                                  onClick={() => onRetryEntry(entry.id)}
                                >
                                  {tLeaderboard(t, 'settlement.retry')}
                                </Button>
                              ) : (
                                <Button
                                  variant="contained"
                                  size="small"
                                  disabled={isSent || isProcessing || isPending}
                                  onClick={() => onPayEntry(entry.id)}
                                >
                                  {isSent
                                    ? tLeaderboard(t, 'settlement.distributedLabel')
                                    : isProcessing || isPending
                                      ? tLeaderboard(t, 'settlement.distributingLabel')
                                      : tLeaderboard(t, 'settlement.distribute')}
                                </Button>
                              )}
                            </PermissionButton>
                            {isSent || isProcessing ? null : (
                              <PermissionButton
                                resource="world-cup-leaderboard-settlement"
                                action="amount"
                              >
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => setOverrideEntry(entry)}
                                >
                                  {tLeaderboard(t, 'settlement.overrideAmount', {
                                    _: 'Override amount',
                                  })}
                                </Button>
                              </PermissionButton>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDateTime(entry.sentAt)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <EmptyText text={tLeaderboard(t, 'settlement.empty')} />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <AmountRequestsPanel api={amountApi} reloadKey={requestsReloadKey} onDecided={onRefresh} />

        {overrideEntry ? (
          <OverrideAmountDialog
            entry={overrideEntry}
            onClose={() => setOverrideEntry(null)}
            onRequest={onRequestAmount}
            onRequested={() => setRequestsReloadKey((k) => k + 1)}
          />
        ) : null}
      </Card>
    );
  }
);

SettlementPanel.displayName = 'SettlementPanel';

const SummaryStat = memo(({ label, value }: { label: string; value: string }) => (
  <Stack>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
      {label}
    </Typography>
    <Typography variant="h6" fontWeight={700}>
      {value}
    </Typography>
  </Stack>
));

SummaryStat.displayName = 'SummaryStat';

// ============ Tier Table (sub-component for tab 2) ============

const TierTable = memo(({ rows, onRowsChange, disabled }: TierTableProps) => {
  const t = useTranslate();
  const { permissions } = usePermissions<PermissionData>();
  const canWrite = hasPermission(permissions?.permissions, 'world-cup-leaderboard', 'write');

  const handleAdd = () => {
    const last = rows[rows.length - 1];
    onRowsChange([...rows, createEmptyTier(last)]);
  };

  const handleRemove = (localId: string) => {
    onRowsChange(rows.filter((item) => item.localId !== localId));
  };

  const handleRowChange = (localId: string, patch: Partial<LeaderboardTierDraft>) => {
    const next = rows.map((item) => (item.localId === localId ? { ...item, ...patch } : item));
    const idx = next.findIndex((item) => item.localId === localId);
    if (idx >= 0) {
      for (let i = idx + 1; i < next.length; i += 1) {
        const prev = next[i - 1];
        const expectedFrom = Number(prev.rangeTo) + 1;
        const currFrom = Number(next[i].rangeFrom);
        if (Number.isFinite(expectedFrom) && currFrom !== expectedFrom) {
          const delta = expectedFrom - currFrom;
          const nextTo = Number(next[i].rangeTo) + delta;
          next[i] = {
            ...next[i],
            rangeFrom: expectedFrom,
            rangeTo: Number.isFinite(nextTo) ? Math.max(nextTo, expectedFrom) : expectedFrom,
          };
        }
      }
    }
    onRowsChange(next);
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="flex-end" alignItems="center">
        <Button
          variant="outlined"
          startIcon={<Iconify icon="eva:plus-fill" />}
          onClick={handleAdd}
          disabled={disabled}
        >
          {tLeaderboard(t, 'actions.addTier')}
        </Button>
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{tLeaderboard(t, 'table.rankRange')}</TableCell>
              <TableCell>{tLeaderboard(t, 'table.rewardPerRank')}</TableCell>
              <TableCell sx={{ width: 120 }}>{tLeaderboard(t, 'table.autoPayout')}</TableCell>
              <TableCell sx={{ width: 100 }}>{tLeaderboard(t, 'table.action')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length ? (
              rows.map((row, index) => {
                const from = Number(row.rangeFrom);
                const to = Number(row.rangeTo);
                const rangeMissing =
                  !Number.isFinite(from) || !Number.isFinite(to) || from <= 0 || to <= 0;
                const rangeBadOrder = !rangeMissing && to < from;
                const prev = rows[index - 1];
                const overlap =
                  !rangeMissing && !rangeBadOrder && !!prev && Number(prev.rangeTo) >= from;
                const rankHelper = rangeMissing
                  ? t('ra.validation.required')
                  : rangeBadOrder
                    ? tLeaderboard(t, 'reward.error.rankRangeInvalid')
                    : overlap
                      ? tLeaderboard(t, 'reward.error.rankRangeOverlap')
                      : undefined;

                return (
                  <TableRow key={row.localId}>
                    <TableCell>
                      <TextInput
                        value={formatRankRange(row)}
                        disabled={disabled}
                        required
                        error={rangeMissing || rangeBadOrder || overlap}
                        // helperText={rankHelper}
                        onChange={(value) => handleRowChange(row.localId, parseRankRange(value))}
                      />
                    </TableCell>
                    <TableCell>
                      <MoneyInput
                        value={row.rewardPerRank}
                        required
                        disabled={disabled}
                        onChange={(value) => handleRowChange(row.localId, { rewardPerRank: value })}
                      />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={row.autoPayout}
                        disabled={disabled || !canWrite}
                        onChange={(_, checked) =>
                          handleRowChange(row.localId, { autoPayout: checked })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <PermissionButton resource="world-cup-leaderboard" action="write">
                        <Button
                          variant="text"
                          color="error"
                          size="small"
                          disabled={disabled}
                          onClick={() => handleRemove(row.localId)}
                        >
                          {tLeaderboard(t, 'actions.delete')}
                        </Button>
                      </PermissionButton>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={4}>
                  <EmptyText text={tLeaderboard(t, 'empty.tiers')} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
});

TierTable.displayName = 'TierTable';

// ============ Shared components ============

const TextInput = memo(
  ({
    value,
    onChange,
    disabled,
    required,
    error,
    helperText,
  }: {
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    required?: boolean;
    error?: boolean;
    helperText?: string;
  }) => (
    <TextField
      size="small"
      fullWidth
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
      required={required}
      error={error}
      helperText={helperText}
    />
  )
);

TextInput.displayName = 'TextInput';

const MoneyInput = memo(
  ({
    value,
    required,
    disabled,
    onChange,
  }: {
    value: string;
    required?: boolean;
    disabled?: boolean;
    onChange: (value: string) => void;
  }) => {
    const t = useTranslate();
    const trimmed = (value ?? '').trim();
    const num = Number(trimmed);
    const isInvalid = required && (!trimmed || !Number.isFinite(num) || num <= 0);

    return (
      <TextField
        size="small"
        fullWidth
        required={required}
        disabled={disabled}
        value={value}
        error={isInvalid}
        // helperText={isInvalid ? t('ra.validation.required') : undefined}
        onChange={(event) => onChange(event.target.value.replace(/[^\d.]/g, ''))}
        InputProps={{
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        }}
        inputProps={{ inputMode: 'decimal' }}
      />
    );
  }
);

MoneyInput.displayName = 'MoneyInput';

const SaveBar = memo(({ loading, onDiscard, onSave, requiredAnyOf }: SaveBarProps) => {
  const t = useTranslate();

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'sticky',
        bottom: 16,
        mt: 3,
        px: 3,
        py: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        zIndex: (theme) => theme.zIndex.appBar,
      }}
    >
      <Stack>
        <Typography variant="subtitle1">{tLeaderboard(t, 'saveBar.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {tLeaderboard(t, 'saveBar.desc')}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1.5}>
        <Button variant="outlined" disabled={loading} onClick={onDiscard}>
          {tLeaderboard(t, 'actions.discard')}
        </Button>

        <PermissionGuard anyOf={requiredAnyOf} fallback={<></>} loadingFallback={<></>}>
          <Button variant="contained" disabled={loading} onClick={onSave}>
            {loading ? t('common.saving') : tLeaderboard(t, 'actions.save')}
          </Button>
        </PermissionGuard>
      </Stack>
    </Paper>
  );
});

SaveBar.displayName = 'SaveBar';

const Field = memo(({ label, children }: FieldProps) => (
  <Stack spacing={1}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    {children}
  </Stack>
));

Field.displayName = 'Field';

const ErrorCard = memo(({ message }: { message: string }) => {
  const t = useTranslate();

  return (
    <Alert severity="error">
      <Typography variant="subtitle2">{tLeaderboard(t, 'error.title')}</Typography>
      <Typography variant="body2">{message}</Typography>
    </Alert>
  );
});

ErrorCard.displayName = 'ErrorCard';

const EmptyText = memo(({ text }: { text: string }) => (
  <Stack alignItems="center" sx={{ py: 4 }}>
    <Typography variant="body2" color="text.secondary">
      {text}
    </Typography>
  </Stack>
));

EmptyText.displayName = 'EmptyText';

const CenterBox = ({ children }: { children: ReactNode }) => (
  <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 360 }}>
    {children}
  </Stack>
);
